import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  getNotifications,
  getHealthRecordFromNotification,
  createShareTokenMFUUser,
} from "../services/mfuUserService";
import { getProfile } from "../services/profileService";
import { initializeLiff, getAndStoreLineData } from "../services/lineService";
import HealthReportModal from "../Components/HealthReportModal";
import PDFViewerModal from "../Components/PDFViewerModal";
import MainHeader from "../Components/MainHeader";

const NotificationsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: "Noto Sans Thai", sans-serif !important;
  * {
    font-family: "Noto Sans Thai", sans-serif !important;
    font-weight: 400;
    color: #666666;
  }
`;

const PageHeader = styled.div`
  background-color: #00bbd3;
  padding: 16px 24px;
  color: white;
  font-size: 18px;
  font-weight: 600;
  font-family: "Noto Sans Thai", sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const NotificationCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const NotificationHeader = styled.div`
  background: #00bbd3;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 400;
`;

const NotificationIcon = styled.div`
  width: 20px;
  height: 20px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const NotificationContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const NotificationImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex-shrink: 0;
`;

const NotificationDivider = styled.div`
  width: 1px;
  height: 45px;
  background-color: #d0d0d0;
  flex-shrink: 0;
  margin: 0 4px;
`;

const NotificationText = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
  color: #8c8c8c;
  margin: 0 0 8px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const NotificationDescription = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  line-height: 1.5;
  margin: 0 0 8px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-weight: 400;
`;

const NotificationTime = styled.span`
  font-size: 12px;
  color: #8c8c8c;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-weight: 400;
`;

const ViewButton = styled.button`
  background: #00bbd3;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-family: "Noto Sans Thai", sans-serif !important;
  cursor: pointer;
  margin-top: 8px;
  margin-right: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #00a3ba;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const DownloadButton = styled.button`
  background: white;
  color: #00bbd3;
  border: 1px solid #00bbd3;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-family: "Noto Sans Thai", sans-serif !important;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #f0f9ff;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 16px;
`;

const NotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationsData, setNotificationsData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [isHealthReportModalOpen, setIsHealthReportModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const navigate = useNavigate();

  const handleProfileClick = () => {
    navigate("/profile-edit");
  };

  const handleViewPDF = async (notification) => {
    try {
      console.log("📄 Opening health report for notification:", notification);
      console.log(
        "📋 Patient info from notificationsData:",
        notificationsData?.patient_info,
      );

      // แปลงข้อมูล notification เป็นรูปแบบที่ modal ต้องการ
      // รวมข้อมูล patient_info จาก notificationsData
      const patientInfo = notificationsData?.patient_info || {};
      const enrichedNotification = {
        ...notification,
        hn: patientInfo.hn || notification.hn,
        nid: patientInfo.nid || notification.nid,
        // แยกชื่อ-นามสกุลจาก name
        firstname: patientInfo.name
          ? patientInfo.name.split(" ")[0]
          : notification.firstname,
        lastname: patientInfo.name
          ? patientInfo.name.split(" ").slice(1).join(" ")
          : notification.lastname,
      };

      const recordData = getHealthRecordFromNotification(enrichedNotification);
      console.log("📊 Transformed record data:", recordData);
      console.log("🧪 Parameters array:", recordData.parameters);

      setSelectedNotification(recordData);
      setIsHealthReportModalOpen(true);
    } catch (err) {
      console.error("Error opening health report:", err);
      alert("เกิดข้อผิดพลาดในการเปิดรายงาน");
    }
  };

  const handleCloseHealthReportModal = () => {
    setIsHealthReportModalOpen(false);
    setSelectedNotification(null);
  };

  const handleDownloadPDF = async (notification) => {
    try {
      console.log("📄 Opening PDF for notification:", notification);

      // ดึง NID จาก notificationsData
      const nid = notificationsData?.patient_info?.nid || notification.nid;

      if (!nid) {
        alert("ไม่พบข้อมูล NID กรุณาลองใหม่อีกครั้ง");
        return;
      }

      console.log("🔗 Creating share token for NID:", nid, "historyId:", notification.id);

      // เรียก API สร้าง share token (MFU User path — ใช้ history_id ดึง visit_date จาก DB)
      const response = await createShareTokenMFUUser(nid, notification.id || null);

      if (response?.status === "success" && response?.data?.share_url) {
        const shareUrl = response.data.share_url;
        console.log("✅ Got share URL:", shareUrl);

        // เปิด PDF ใน modal แทนการเปิดในแท็บใหม่
        setPdfUrl(shareUrl);
        setIsPDFViewerOpen(true);
      } else {
        throw new Error("ไม่ได้รับ share URL จาก server");
      }
    } catch (err) {
      console.error("Error opening PDF:", err);
      alert(
        "เกิดข้อผิดพลาดในการเปิด PDF\n" +
          (err.message || "กรุณาลองใหม่อีกครั้ง")
      );
    }
  };

  const handleClosePDFViewer = () => {
    setIsPDFViewerOpen(false);
    setPdfUrl(null);
  };

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("🔔 Loading notifications and profile...");

        // Initialize LIFF first
        const liffInitialized = await initializeLiff();
        if (liffInitialized) {
          // Get and store LINE data
          await getAndStoreLineData();
          console.log("✅ LIFF initialized and LINE data stored");
        } else {
          console.warn("⚠️ LIFF initialization failed, using localStorage data");
        }

        // Check if user has NID (must login first)
        const username = localStorage.getItem('username');
        if (!username) {
          console.warn("⚠️ No NID found, redirecting to login");
          setError("กรุณา Login ก่อนดูผลสุขภาพ");
          setTimeout(() => {
            navigate('/');
          }, 2000);
          return;
        }

        // Load profile and notifications in parallel
        const [profileRes, notificationsRes] = await Promise.all([
          getProfile().catch((err) => {
            console.error("Profile load error:", err);
            return null;
          }),
          getNotifications().catch((err) => {
            console.error("Notifications load error:", err);
            return null;
          }),
        ]);

        console.log("Profile data:", profileRes);
        console.log("Notifications data:", notificationsRes);

        // 🔍 Log detailed notification info
        if (notificationsRes?.data?.notifications) {
          const notifications = notificationsRes.data.notifications;
          console.log("📋 Total notifications received:", notifications.length);

          if (notifications.length > 0) {
            console.log("🕐 Latest notification:");
            console.log("  - ID:", notifications[0].id);
            console.log("  - Sent at:", notifications[0].sent_at);
            console.log("  - Date:", notifications[0].date);
            console.log("  - Time:", notifications[0].time);

            console.log("📜 All notification timestamps:");
            notifications.forEach((n, index) => {
              console.log(`  ${index + 1}. ID: ${n.id} | ${n.sent_at} (${n.date} ${n.time})`);
            });
          }
        }

        if (profileRes) {
          let profile = profileRes.data || profileRes;

          // Convert image path to Firebase URL
          let imagePath = profile.image || profile.profile_image || "";
          let imageUrl = null;
          if (imagePath && imagePath !== "") {
            if (imagePath.startsWith("http")) {
              imageUrl = imagePath;
            } else {
              imageUrl = `https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/${imagePath}`;
            }
          }

          setProfileData({
            ...profile,
            profileImage: imageUrl,
          });
        }

        if (notificationsRes?.status === "success" || notificationsRes?.data) {
          setNotificationsData(notificationsRes.data);
        }
      } catch (err) {
        console.error("❌ Error loading data:", err);
        setError(err.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const BellIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
        fill="white"
      />
    </svg>
  );

  // Get profile name and image
  const profileName =
    notificationsData?.patient_info?.name ||
    profileData?.name ||
    profileData?.firstname ||
    "ผู้ใช้";
  const profileImage = profileData?.profileImage || "./person.png";

  // Get notifications list
  const notifications = notificationsData?.notifications || [];

  // Show loading state
  if (loading) {
    return (
      <NotificationsContainer>
        <MainHeader />
        <PageHeader>แจ้งเตือน</PageHeader>
        <LoadingContainer>กำลังโหลดข้อมูล...</LoadingContainer>
      </NotificationsContainer>
    );
  }

  return (
    <NotificationsContainer>
      <MainHeader />

      <PageHeader>แจ้งเตือน</PageHeader>

      <Content>
        {error && <ErrorMessage>เกิดข้อผิดพลาด: {error}</ErrorMessage>}

        {notifications.length === 0 && !error && (
          <EmptyMessage>ยังไม่มีการแจ้งเตือน</EmptyMessage>
        )}

        {notifications.map((notification) => (
          <NotificationCard key={notification.id}>
            <NotificationHeader>
              <NotificationIcon>
                <BellIcon />
              </NotificationIcon>
              {notification.type === "health_result"
                ? "ผลการตรวจสุขภาพ"
                : notification.type === "appointment"
                  ? "กำหนดนัดฉีดวัคซีน"
                  : "แจ้งเตือน"}
            </NotificationHeader>

            <NotificationContent>
              <NotificationImage
                src={
                  notification.type === "health_result"
                    ? "./PDF.png"
                    : "./เข็มฉีดยา.png"
                }
                alt=""
              />
              <NotificationDivider />
              <NotificationText>
                <NotificationTitle>{notification.title}</NotificationTitle>
                <NotificationDescription>
                  {notification.message}
                </NotificationDescription>
                <NotificationTime>
                  {notification.date} | {notification.time}
                  {notification.sent_by && ` | ส่งโดย ${notification.sent_by}`}
                </NotificationTime>
                {notification.has_pdf && (
                  <div>
                    <ViewButton onClick={() => navigate('/recommendations', { state: { visitDate: notification.checkup_date } })}>
                      ดูคำแนะนำ
                    </ViewButton>
                    <DownloadButton onClick={() => handleDownloadPDF(notification)}>
                      ดาวน์โหลด PDF
                    </DownloadButton>
                  </div>
                )}
              </NotificationText>
            </NotificationContent>
          </NotificationCard>
        ))}
      </Content>

      <HealthReportModal
        isOpen={isHealthReportModalOpen}
        onClose={handleCloseHealthReportModal}
        recordData={selectedNotification}
      />

      <PDFViewerModal
        isOpen={isPDFViewerOpen}
        onClose={handleClosePDFViewer}
        pdfUrl={pdfUrl}
        title="ผลการตรวจสุขภาพ"
      />
    </NotificationsContainer>
  );
};

export default NotificationsPage;
